import { useEffect, useRef, useCallback } from 'react'
import { Centrifuge } from 'centrifuge'
import { api } from '../api'

// Dynamic WebSocket URL: tự theo domain hiện tại khi production, dùng env khi dev
const WS_URL = process.env.REACT_APP_CENTRIFUGO_WS ||
  `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/connection/websocket`

export function useCentrifuge(onMessage) {
  const clientRef = useRef(null)
  const onMessageRef = useRef(onMessage)
  // Lưu tất cả channel muốn subscribe — dùng để retry khi client reconnect
  const wantedChannels = useRef(new Set())

  useEffect(() => { onMessageRef.current = onMessage })

  // Tạo subscription thật sự trên client (internal)
  const doSubscribe = useCallback((client, channel) => {
    if (client.getSubscription(channel)) return
    const sub = client.newSubscription(channel, {
      // Centrifugo v5 yêu cầu subscription token riêng (có claim "channel")
      getToken: async () => {
        const { token } = await api.centrifugoSubToken(channel)
        return token
      },
    })
    sub.on('publication', (ctx) => {
      onMessageRef.current(channel, ctx.data)
    })
    sub.on('subscribed', () => {
      console.log('[sub] OK:', channel)
    })
    sub.on('error', (ctx) => {
      console.error('[sub] LỖI:', channel, ctx)
    })
    sub.subscribe()
  }, [])

  useEffect(() => {
    let cachedToken = null
    let tokenExpiry = 0

    const client = new Centrifuge(WS_URL, {
      getToken: async () => {
        if (cachedToken && Date.now() < tokenExpiry) return cachedToken
        const { token } = await api.centrifugoToken()
        cachedToken = token
        tokenExpiry = Date.now() + 50 * 60 * 1000
        return token
      },
    })

    client.on('connected', () => {
      console.log('[Centrifugo] Đã kết nối — subscribe', wantedChannels.current.size, 'channel')
      // Khi connected (hoặc reconnected), subscribe lại tất cả channel đang cần
      wantedChannels.current.forEach(ch => doSubscribe(client, ch))
    })
    client.on('disconnected', (ctx) => console.log('[Centrifugo] Mất kết nối', ctx.reason))
    client.on('error', (ctx) => console.log('[Centrifugo] Lỗi', ctx))

    client.connect()
    clientRef.current = client

    return () => {
      client.disconnect()
      clientRef.current = null
    }
  }, [doSubscribe])

  const subscribe = useCallback((channel) => {
    // Luôn thêm vào wantedChannels — đảm bảo được subscribe khi client connect/reconnect
    wantedChannels.current.add(channel)
    const client = clientRef.current
    if (client) {
      doSubscribe(client, channel)
    }
    // Nếu client chưa sẵn sàng, sẽ được subscribe trong handler 'connected' ở trên
  }, [doSubscribe])

  const unsubscribe = useCallback((channel) => {
    wantedChannels.current.delete(channel)
    const client = clientRef.current
    if (!client) return
    const sub = client.getSubscription(channel)
    if (sub) {
      sub.unsubscribe()
      client.removeSubscription(sub)
      console.log('[unsubscribe]', channel)
    }
  }, [])

  return { subscribe, unsubscribe }
}
